import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getComments, updateCommentStatus, deleteComment } from '@/lib/api';
import { formatDate, truncate } from '@/lib/utils';
import { toast } from 'sonner';
import { Check, X, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react';

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const fetchComments = async () => {
    try {
      const response = await getComments();
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleUpdateStatus = async (commentId, status) => {
    try {
      await updateCommentStatus(commentId, status);
      toast.success(`Comment ${status}`);
      fetchComments();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleDelete = async () => {
    if (!commentToDelete) return;
    
    try {
      await deleteComment(commentToDelete.id);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  const filteredComments = comments.filter(c => {
    if (activeTab === 'all') return true;
    return c.status === activeTab;
  });

  const pendingCount = comments.filter(c => c.status === 'pending').length;
  const approvedCount = comments.filter(c => c.status === 'approved').length;
  const rejectedCount = comments.filter(c => c.status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-comments-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-brand-blue">Comment Moderation</h1>
        <p className="text-gray-500 mt-1">Review and moderate blog comments</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pending
            {pendingCount > 0 && (
              <Badge className="bg-brand-orange text-white">{pendingCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle size={16} />
            Approved ({approvedCount})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2">
            <XCircle size={16} />
            Rejected ({rejectedCount})
          </TabsTrigger>
          <TabsTrigger value="all">All ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card className="bg-white rounded-xl">
            <CardContent className="p-0">
              {filteredComments.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-gray-500">No comments found.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Author</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead>Post</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredComments.map((comment) => (
                      <TableRow key={comment.id} data-testid={`comment-row-${comment.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-brand-blue">{comment.author_name}</p>
                            <p className="text-gray-400 text-xs">{comment.author_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-gray-600 text-sm">{truncate(comment.content, 100)}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-gray-500 text-sm">{truncate(comment.blog_post_title, 30)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            comment.status === 'approved' ? 'bg-green-100 text-green-700' :
                            comment.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {comment.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {formatDate(comment.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {comment.status !== 'approved' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => handleUpdateStatus(comment.id, 'approved')}
                                data-testid={`approve-${comment.id}`}
                              >
                                <Check size={16} />
                              </Button>
                            )}
                            {comment.status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => handleUpdateStatus(comment.id, 'rejected')}
                                data-testid={`reject-${comment.id}`}
                              >
                                <X size={16} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-600 hover:bg-gray-50"
                              onClick={() => {
                                setCommentToDelete(comment);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-${comment.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminComments;
